import { useEffect, useRef, useState } from "react";
import { W2, Reveal } from "../site/fx";
import ServeStrip from "../site/ServeStrip";
import { useSEO } from "../seo";

const REASONS = [
  { t: "Food-grade stainless steel", d: "Food-grade, high-quality stainless steel for hygiene, corrosion resistance and lasting durability." },
  { t: "Reliable B2B supply", d: "Consistent quality and on-time delivery, whether you order 100 pieces or 10,000." },
  { t: "Customisation & OEM", d: "Custom design and private label, with size, finish, branding and packaging to your requirements." },
  { t: "Precise, elegant design", d: "Clean aesthetics and user-friendly details that improve presentation and service." },
  { t: "Factory-direct pricing", d: "Value that helps you manage budgets without compromising on quality." },
];

const MILESTONES = [
  { year: "1970", text: "The family begins working with stainless steel." },
  { year: "Today", text: "Sepla supplies hotels, restaurants, caterers and distributors with a 46-product hotelware range." },
];

export default function About() {
  useSEO("About — Sepla Hotelware", "Sepla designs and manufactures stainless steel hotelware for hotels, restaurants, caterers and distributors. Crafting steel since 1970.");
  const tlRef = useRef(null);
  const [tlProg, setTlProg] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = tlRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const p = Math.min(1, Math.max(0, (window.innerHeight * 0.7 - r.top) / r.height));
      setTlProg(p);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <main className="page" data-testid="about-page">
      <header className="page-head">
        <p className="kicker">About Sepla</p>
        <W2 as="h1" parts={[{ t: "Crafting " }, { t: "excellence", em: true }, { t: " in stainless steel hotelware" }]} className="page-title" />
        <Reveal><p className="page-sub wide">
          Sepla designs and manufactures stainless steel hotelware for hotels, restaurants, caterers and distributors.
          Every piece is made for durability, hygiene and good presentation, and to stand up to daily professional use.
          From cereal and juice dispensers to ice buckets and serving bowls, we supply complete buffet and table solutions.
          We support new properties and upgrades alike, including custom and private-label work.
        </p></Reveal>
      </header>

      <section className="sect sect-dark">
        <div className="sect-in">
          <p className="kicker">Why choose us</p>
          <W2 parts={[{ t: "Made for the " }, { t: "pace", em: true }, { t: " of professional kitchens" }]} className="sect-title" />
          <Reveal className="reason-list">
            {REASONS.map((r, i) => (
              <article key={r.t} className="reason-row" data-testid={`about-reason-${i + 1}`}>
                <span className="reason-num">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{r.t}</h3>
                  <p>{r.d}</p>
                </div>
              </article>
            ))}
          </Reveal>
        </div>
      </section>

      <ServeStrip />

      <section className="sect sect-light">
        <div className="sect-in">
          <p className="kicker">Since 1970</p>
          <W2 parts={[{ t: "Five decades of " }, { t: "steel", em: true }]} className="sect-title" />
          <div className="timeline" ref={tlRef} data-testid="timeline">
            <span className="timeline-rail" aria-hidden="true"><i style={{ transform: `scaleY(${tlProg})` }} /></span>
            {MILESTONES.map((m, i) => (
              <Reveal key={m.year} delay={i * 60} className="milestone">
                <span className="milestone-year">{m.year}</span>
                <p>{m.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
